// Live Chat Operator Dashboard
//
// Three-pane layout:
//   • Left rail   — queue (FIFO with claim button), my active
//                   sessions, and a presence toggle.
//   • Centre      — active conversation transcript + composer +
//                   file attach button (5 MB cap, base64 data URL).
//   • Right rail  — visitor info: name, email, IP, user-agent, and
//                   the bot transcript that preceded the handoff.
//
// Real-time fanout via /ws/live-chat (queue:{org}, operator:{me},
// session:{active}). Polling is kept as a low-frequency safety net in
// case the socket drops. Cadences:
//   queue + my sessions ......... 15 s safety poll
//   active session transcript ... 10 s safety poll
//   operator presence ........... 30 s heartbeat (when online/away)
//
// Queue arrival notifications are owned by the authenticated shell hook.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLiveChatNotificationTarget } from "@/components/live-chat-notification-bell";
import { useLiveMessageRead } from "@/hooks/use-live-message-read";
import { useLiveChatReconciliation } from "@/hooks/use-live-chat-reconciliation";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Headphones, Send, Paperclip, X, Circle, Clock, MessageSquare,
  User as UserIcon, Globe, Mail, MonitorSmartphone, Inbox,
} from "lucide-react";
import type { LiveChatSession, LiveChatMessage } from "@shared/schema";

type SessionDetail = {
  session: LiveChatSession;
  messages: LiveChatMessage[];
  botTranscript: { role: string; content: string; createdAt: string | null }[];
  assignedOperator: { id: string; name: string; email: string } | null;
};

function relativeTime(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const t = typeof iso === "string" ? new Date(iso).getTime() : iso.getTime();
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export default function LiveChatPage() {
  useLiveChatReconciliation();
  const { user, organization } = useAuth();
  const { toast } = useToast();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const target = useLiveChatNotificationTarget((id) => setActiveSessionId(id));
  const highlightedQueueRef = useRef<HTMLButtonElement>(null);
  const [composer, setComposer] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Queue ───────────────────────────────────────────────────────
  const queueQuery = useQuery<LiveChatSession[]>({
    queryKey: ["/api/live-chat/queue"],
    refetchInterval: 15000,
  });

  // ── My active sessions ──────────────────────────────────────────
  const mineQuery = useQuery<LiveChatSession[]>({
    queryKey: ["/api/live-chat/sessions/mine"],
    refetchInterval: 15000,
  });

  // ── Active session detail ───────────────────────────────────────
  const detailQuery = useQuery<SessionDetail>({
    queryKey: ["/api/live-chat/sessions", activeSessionId],
    enabled: !!activeSessionId,
    refetchInterval: 10000,
  });

  // ── Real-time fanout via /ws/live-chat ──────────────────────────
  useLiveMessageRead(activeSessionId, detailQuery.data?.messages);
  // Three sockets:
  //   queue:{org}     — new visitor sessions appear / get claimed
  //   operator:{me}   — my own claim notifications across tabs
  //   session:{id}    — active transcript updates while open
  // The socket triggers query invalidation; the queries themselves
  // remain the source of truth so we don't have to mirror the data
  // model client-side.
  useEffect(() => {
    const orgId = organization?.id;
    const meId = user?.id;
    if (!orgId && !meId) return;
    const token = (() => { try { return localStorage.getItem("dotnetbot.jwt") || ""; } catch { return ""; } })();
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const base = `${proto}://${window.location.host}/ws/live-chat`;
    const sockets: WebSocket[] = [];
    const open = (topic: string, onEvent: (ev: string, payload: unknown) => void) => {
      try {
        const url = `${base}?topic=${encodeURIComponent(topic)}${token ? `&access_token=${encodeURIComponent(token)}` : ""}`;
        const ws = new WebSocket(url);
        ws.onmessage = (m) => {
          try {
            const env = JSON.parse(m.data);
            onEvent(env.event, env.payload);
          } catch { /* ignore */ }
        };
        sockets.push(ws);
      } catch { /* ignore */ }
    };
    if (meId) {
      open(`operator:${meId}`, () => {
        queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      });
    }
    return () => { for (const s of sockets) try { s.close(); } catch { /* ignore */ } };
  }, [user?.id, organization?.id]);

  // Per-session socket — re-opens when the operator switches sessions.
  useEffect(() => {
    if (!activeSessionId) return;
    const token = (() => { try { return localStorage.getItem("dotnetbot.jwt") || ""; } catch { return ""; } })();
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/ws/live-chat?topic=${encodeURIComponent(`session:${activeSessionId}`)}${token ? `&access_token=${encodeURIComponent(token)}` : ""}`;
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(url);
      ws.onmessage = () => {
        queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] });
      };
    } catch { /* ignore */ }
    return () => { try { ws?.close(); } catch { /* ignore */ } };
  }, [activeSessionId]);

  // ── Presence ────────────────────────────────────────────────────
  const presenceQuery = useQuery<{ status: string; note: string | null }>({
    queryKey: ["/api/live-chat/operator-status"],
  });
  const setPresence = useMutation({
    mutationFn: async (status: string) => {
      const res = await apiRequest("POST", "/api/live-chat/operator-status", { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/operator-status"] });
    },
    onError: (e: any) => {
      toast({ title: "Status unchanged", description: String(e?.message ?? e), variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/operator-status"] });
    },
  });
  // An operator can't go busy/offline while a chat is still in progress.
  const hasOpenChat = (mineQuery.data || []).some(s => s.status === "active" || s.status === "escalated");

  // Heartbeat: re-stamp the presence row every 30 s while we're
  // online or away so other operators can tell who's actually around.
  useEffect(() => {
    const status = presenceQuery.data?.status;
    if (status !== "online" && status !== "away") return;
    const i = window.setInterval(() => {
      setPresence.mutate(status);
    }, 30000);
    return () => window.clearInterval(i);
  }, [presenceQuery.data?.status]);

  // Auto-set to "online" the first time the page loads if we're
  // currently offline — operators expect to be available as soon as
  // they open the dashboard.
  useEffect(() => {
    if (presenceQuery.isLoading) return;
    if (presenceQuery.data?.status === "offline" || presenceQuery.data === undefined) {
      setPresence.mutate("online");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceQuery.isLoading]);

  const requestNotificationPermission = () => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch {/* ignore */}
  };
  useEffect(requestNotificationPermission, []);

  // ── Mutations: claim, send, upload, close ───────────────────────
  const claimNext = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/live-chat/sessions/claim-next");
      return res.json() as Promise<LiveChatSession>;
    },
    onSuccess: (sess) => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      setActiveSessionId(sess.id);
      toast({ title: "Chat claimed", description: `You are now connected to ${sess.visitorName ?? "the visitor"}.` });
    },
    onError: (e: any) => toast({ title: "Could not claim", description: String(e?.message ?? e), variant: "destructive" }),
  });

  const claimSpecific = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/live-chat/sessions/${id}/claim`);
      return res.json() as Promise<LiveChatSession>;
    },
    onSuccess: (sess) => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      setActiveSessionId(sess.id);
    },
    onError: (e: any) => toast({ title: "Could not claim", description: String(e?.message ?? e), variant: "destructive" }),
  });

  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      if (!activeSessionId) throw new Error("No active session");
      const res = await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/messages`, { content });
      return res.json();
    },
    onSuccess: () => {
      setComposer("");
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] });
    },
    onError: (e: any) => toast({ title: "Send failed", description: String(e?.message ?? e), variant: "destructive" }),
  });

  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      if (!activeSessionId) throw new Error("No active session");
      if (file.size > 5 * 1024 * 1024) throw new Error("File exceeds 5 MB limit");
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const res = await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/upload`, {
        dataUrl, fileName: file.name, mime: file.type,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] });
    },
    onError: (e: any) => toast({ title: "Upload failed", description: String(e?.message ?? e), variant: "destructive" }),
  });

  const closeSession = useMutation({
    mutationFn: async () => {
      if (!activeSessionId) throw new Error("No active session");
      const res = await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/close`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] });
      toast({ title: "Chat ended" });
    },
  });

  // Auto-scroll the transcript when new messages arrive.
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detailQuery.data?.messages.length]);

  // Auto-select my first active session when there's no active selection.
  useEffect(() => {
    if (!target.requestedSessionId && !activeSessionId && mineQuery.data && mineQuery.data.length > 0) {
      setActiveSessionId(mineQuery.data[0].id);
    }
  }, [mineQuery.data, activeSessionId, target.requestedSessionId]);

  useEffect(() => {
    if (target.queuedSessionId) highlightedQueueRef.current?.scrollIntoView({ block: "nearest" });
  }, [target.queuedSessionId, queueQuery.data]);

  const canActOnSession = detailQuery.data?.session.assignedOperatorId === user?.id;

  const presenceColor = useMemo(() => {
    const s = presenceQuery.data?.status ?? "offline";
    if (s === "online") return "text-green-500";
    if (s === "away") return "text-yellow-500";
    return "text-gray-400";
  }, [presenceQuery.data?.status]);

  return (
    <div className="flex h-[calc(100vh-3rem)] bg-background" data-testid="page-live-chat">
      {/* ─────── LEFT RAIL ─────── */}
      <aside className="w-72 border-r flex flex-col bg-gradient-to-b from-muted/30 to-transparent">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm" style={{ background: "linear-gradient(135deg, hsl(160 70% 45%), hsl(170 70% 40%))" }}>
              <Headphones className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold leading-tight">Live Chat</h2>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Operator console</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Circle className={`w-3 h-3 fill-current ${presenceColor}`} />
            <select
              value={presenceQuery.data?.status ?? "offline"}
              onChange={(e) => setPresence.mutate(e.target.value)}
              className="flex-1 text-xs bg-transparent border rounded px-2 py-1 hover-elevate cursor-pointer"
              aria-label="Presence status"
              data-testid="select-presence"
            >
              <option value="online">Online</option>
              <option value="away">Away</option>
              <option value="offline" disabled={hasOpenChat}>Offline{hasOpenChat ? " (in chat)" : ""}</option>
            </select>
          </div>
        </div>

        <div className="p-3 border-b">
          {!activeSessionId && (target.message || target.isResolving) && (
            <div className="mb-3 rounded-md border bg-muted/40 p-2.5 text-xs">
              <p role="status">{target.isResolving ? "Checking request…" : target.message}</p>
              {!target.isResolving && !target.queuedSessionId && <Button variant="ghost" size="sm" className="mt-1" onClick={target.retry}>Try again</Button>}
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Queue</span>
            <Badge variant="secondary" data-testid="badge-queue-count">{queueQuery.data?.length ?? 0}</Badge>
          </div>
          <Button
            size="sm"
            className="w-full mb-2"
            onClick={() => claimNext.mutate()}
            disabled={!queueQuery.data?.length || claimNext.isPending}
            data-testid="button-claim-next"
          >
            {claimNext.isPending ? "Claiming…" : "Claim next"}
          </Button>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <div className="space-y-1 max-h-48 overflow-y-auto" tabIndex={0} role="region" aria-label="Queue">
            {queueQuery.data?.length === 0 && (
              <p className="text-[11px] text-muted-foreground text-center py-3" data-testid="text-queue-empty">No one in queue</p>
            )}
            {queueQuery.data?.map((s) => (
              <button
                key={s.id}
                ref={s.id === target.queuedSessionId ? highlightedQueueRef : undefined}
                onClick={() => claimSpecific.mutate(s.id)}
                disabled={claimSpecific.isPending}
                className={`w-full text-left p-2 rounded-md text-xs hover-elevate ${s.id === target.queuedSessionId ? "bg-accent ring-2 ring-primary" : ""}`}
                aria-label={s.id === target.queuedSessionId ? "Claim highlighted request" : undefined}
                data-testid={`button-queue-${s.id}`}
              >
                <div className="flex items-center gap-1.5 font-medium truncate">
                  <UserIcon className="w-3 h-3 shrink-0" />
                  {s.visitorName ?? "Visitor"}
                </div>
                <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
                  <Clock className="w-3 h-3" />
                  {relativeTime(s.requestedAt)}
                  {s.id === target.queuedSessionId && <span className="ml-auto font-medium text-primary">Claim</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div className="p-3 flex-1 overflow-y-auto" tabIndex={0} role="region" aria-label="My active chats">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">My active</span>
            <Badge variant="outline" data-testid="badge-active-count">{mineQuery.data?.length ?? 0}</Badge>
          </div>
          <div className="space-y-1">
            {mineQuery.data?.length === 0 && (
              <p className="text-[11px] text-muted-foreground text-center py-3">No active chats</p>
            )}
            {mineQuery.data?.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSessionId(s.id)}
                className={`w-full text-left p-2 rounded-md text-xs hover-elevate ${activeSessionId === s.id ? "bg-accent" : ""}`}
                data-testid={`button-active-${s.id}`}
              >
                <div className="flex items-center gap-1.5 font-medium truncate">
                  <MessageSquare className="w-3 h-3 shrink-0" />
                  {s.visitorName ?? "Visitor"}
                </div>
                <div className="text-muted-foreground mt-0.5">
                  {relativeTime(s.lastVisitorAt ?? s.lastOperatorAt ?? s.claimedAt)}
                </div>
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* ─────── CENTRE ─────── */}
      <section aria-label="Chat session" className="flex-1 flex flex-col min-w-0">
        {!activeSessionId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            <div className="text-center">
              <Headphones className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p data-testid="text-empty-state">Claim a chat from the queue to get started.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="h-12 flex items-center justify-between px-4 border-b shrink-0">
              <div>
                <h3 className="text-sm font-semibold" data-testid="text-active-visitor">
                  {detailQuery.data?.session.visitorName ?? "Visitor"}
                </h3>
                <p className="text-[11px] text-muted-foreground">
                  {detailQuery.data?.session.status === "active" ? "Active" : detailQuery.data?.session.status}
                  {" · "}{relativeTime(detailQuery.data?.session.claimedAt)}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => closeSession.mutate()}
                disabled={!canActOnSession || detailQuery.data?.session.status !== "active"}
                data-testid="button-close-session"
              >
                <X className="w-3.5 h-3.5 mr-1" />End
              </Button>
            </div>

            <ScrollArea className="flex-1 px-4 py-3">
              <div className="max-w-2xl mx-auto space-y-3">
                {detailQuery.data?.messages.map((m) => (
                  <Bubble key={m.id} m={m} mineId={user?.id ?? ""} />
                ))}
                <div ref={transcriptEndRef} />
              </div>
            </ScrollArea>

            <div className="p-3 border-t shrink-0">
              <div className="max-w-2xl mx-auto flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadFile.mutate(f);
                    e.target.value = "";
                  }}
                  data-testid="input-file"
                />
                <IconButton
                  variant="outline"
                  label="Attach file"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canActOnSession || uploadFile.isPending || detailQuery.data?.session.status !== "active"}
                  data-testid="button-attach"
                >
                  <Paperclip className="w-4 h-4" aria-hidden="true" />
                </IconButton>
                <Input
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (composer.trim()) sendMessage.mutate(composer.trim());
                    }
                  }}
                  placeholder="Type a reply…"
                  disabled={!canActOnSession || detailQuery.data?.session.status !== "active"}
                  data-testid="input-composer"
                />
                <Button
                  onClick={() => composer.trim() && sendMessage.mutate(composer.trim())}
                  disabled={!canActOnSession || !composer.trim() || sendMessage.isPending || detailQuery.data?.session.status !== "active"}
                  aria-label="Send message"
                  data-testid="button-send"
                >
                  <Send className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ─────── RIGHT RAIL ─────── */}
      {activeSessionId && detailQuery.data && (
        <aside className="w-72 border-l overflow-y-auto p-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Visitor</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1.5">
              <Field icon={UserIcon} label="Name" value={detailQuery.data.session.visitorName ?? "Anonymous"} testId="text-visitor-name" />
              <Field icon={Mail} label="Email" value={detailQuery.data.session.visitorEmail ?? "—"} testId="text-visitor-email" />
              <Field icon={Globe} label="IP" value={detailQuery.data.session.visitorIp ?? "—"} testId="text-visitor-ip" />
              <Field icon={Globe} label="Country" value={detailQuery.data.session.visitorCountry ?? "Unknown"} testId="text-visitor-country" />
              <Field icon={MonitorSmartphone} label="Browser" value={shortenUA(detailQuery.data.session.visitorUserAgent)} testId="text-visitor-ua" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Bot transcript</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-2 max-h-80 overflow-y-auto">
              {detailQuery.data.botTranscript.length === 0 && (
                <p className="text-muted-foreground">No prior bot conversation.</p>
              )}
              {detailQuery.data.botTranscript.map((m, i) => (
                <div key={i}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.role}</div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </aside>
      )}
    </div>
  );
}

function Bubble({ m, mineId }: { m: LiveChatMessage; mineId: string }) {
  if (m.sender === "system") {
    return (
      <div className="text-center text-[11px] text-muted-foreground italic" data-testid={`msg-system-${m.id}`}>
        {m.content}
      </div>
    );
  }
  const mine = m.sender === "operator" && (m.operatorId === mineId || !mineId);
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.sender}-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
        {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
        {m.attachmentUrl && (
          <div className="mt-1">
            {m.attachmentMime?.startsWith("image/") ? (
              <img src={m.attachmentUrl} alt={m.attachmentName ?? "attachment"} className="max-w-full max-h-64 rounded-md" />
            ) : (
              <a href={m.attachmentUrl} download={m.attachmentName ?? "file"} className="underline text-xs">
                {m.attachmentName ?? "Download file"}
              </a>
            )}
          </div>
        )}
        <div className="text-[10px] opacity-60 mt-1">{relativeTime(m.createdAt)}</div>
      </div>
    </div>
  );
}

function Field({
  icon: Icon, label, value, testId,
}: { icon: any; label: string; value: string; testId?: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3 h-3 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="truncate" data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}

function shortenUA(ua: string | null): string {
  if (!ua) return "—";
  // Very rough: show "Browser on OS" inferred from common UA patterns.
  let browser = "Browser";
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/")) browser = "Chrome";
  else if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Safari/")) browser = "Safari";
  let os = "";
  if (ua.includes("Mac OS X")) os = "macOS";
  else if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  return os ? `${browser} on ${os}` : browser;
}
